import React, { useState, useEffect } from 'react';
import { supabaseClient } from '../../services/supabase';

// 必要なテーブル数を計算（大人5名以上で2卓必要）
const getRequiredTableCount = (adultCount) => {
  return adultCount >= 5 ? 2 : 1;
};

// ユーザーサイトのAPIエンドポイント
const USER_SITE_API = process.env.REACT_APP_USER_SITE_URL || 'https://yoshizushi-asahikawa.com';

// 日付をフォーマット（表示用）
const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const weekDays = ['日', '月', '火', '水', '木', '金', '土'];
  const weekDay = weekDays[date.getDay()];
  return `${year}年${month}月${day}日(${weekDay})`;
};

// 時間をフォーマット
const formatTime = (timeStr) => {
  if (!timeStr) return '';
  return timeStr.substring(0, 5);
};

// メール送信API呼び出し
const sendEmail = async (type, reservation) => {
  try {
    const response = await fetch(`${USER_SITE_API}/api/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ type, reservation }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'メール送信に失敗しました');
    }

    return response.json();
  } catch (error) {
    console.error('メール送信エラー:', error);
    throw error;
  }
};

function EditReservationModal({ reservation, onClose, onUpdate }) {
  const [formData, setFormData] = useState({
    reservation_date: '',
    reservation_time: '',
    adult_count: 0,
    child_count: 0,
    seat_type: 'counter',
    status: 'pending',
  });
  const [loading, setLoading] = useState(false);
  const [prevStatus, setPrevStatus] = useState('');

  useEffect(() => {
    if (reservation) {
      setFormData({
        reservation_date: reservation.reservation_date || '',
        reservation_time: reservation.reservation_time || '',
        adult_count: reservation.adult_count || 0,
        child_count: reservation.child_count || 0,
        seat_type: reservation.seat_type || 'counter',
        status: reservation.status || 'pending',
      });
      setPrevStatus(reservation.status || 'pending');
    }
  }, [reservation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // テーブル席の場合、必要な卓数を計算
      const adultCount = parseInt(formData.adult_count);
      const tableCount = formData.seat_type === 'table' ? getRequiredTableCount(adultCount) : null;

      const updates = {
        reservation_date: formData.reservation_date,
        reservation_time: formData.reservation_time,
        adult_count: adultCount,
        child_count: parseInt(formData.child_count),
        seat_type: formData.seat_type,
        status: formData.status,
      };

      // table_countカラムが存在する場合のみ追加
      // カラムが存在しない場合のエラーを回避するため、try-catchで処理
      if (tableCount !== null) {
        updates.table_count = tableCount;
      }

      const { error } = await supabaseClient
        .from('reservations')
        .update(updates)
        .eq('id', reservation.id);

      if (error) {
        // table_countカラムが存在しない場合のエラーを特別に処理
        if (error.code === 'PGRST204' && error.message.includes('table_count')) {
          // table_countを除いて再試行
          delete updates.table_count;
          const { error: retryError } = await supabaseClient
            .from('reservations')
            .update(updates)
            .eq('id', reservation.id);
          if (retryError) throw retryError;
        } else {
          throw error;
        }
      }

      // ステータスがconfirmedに変更された場合、確定メール自動送信
      if (
        formData.status === 'confirmed' &&
        prevStatus !== 'confirmed' &&
        reservation.customer_email
      ) {
        try {
          await sendEmail('reservation_confirmed', {
            code: reservation.reservation_code,
            date: reservation.reservation_date,
            dateDisplay: formatDateForDisplay(reservation.reservation_date),
            time: formatTime(reservation.reservation_time),
            name: reservation.customer_name,
            adultCount: reservation.adult_count,
            childCount: reservation.child_count,
            seatType: reservation.seat_type,
            email: reservation.customer_email,
            phone: reservation.customer_phone,
          });
          alert('予約を更新し、確定メールを送信しました。');
        } catch (emailError) {
          console.error('確定メール送信エラー:', emailError);
          alert('予約は更新しましたが、メール送信に失敗しました。');
        }
      }
      // ステータスがcancelledに変更された場合、キャンセルメール自動送信
      else if (
        formData.status === 'cancelled' &&
        prevStatus !== 'cancelled' &&
        reservation.customer_email
      ) {
        try {
          await sendEmail('reservation_cancelled_by_admin', {
            code: reservation.reservation_code,
            date: reservation.reservation_date,
            dateDisplay: formatDateForDisplay(reservation.reservation_date),
            time: formatTime(reservation.reservation_time),
            name: reservation.customer_name,
            adultCount: reservation.adult_count,
            childCount: reservation.child_count,
            seatType: reservation.seat_type,
            email: reservation.customer_email,
            phone: reservation.customer_phone,
          });
          alert('予約を更新し、キャンセルメールを送信しました。');
        } catch (emailError) {
          console.error('キャンセルメール送信エラー:', emailError);
          alert('予約は更新しましたが、メール送信に失敗しました。');
        }
      } else {
        alert('予約を更新しました。');
      }

      onUpdate();
      onClose();
    } catch (error) {
      console.error('予約更新エラー:', error);
      alert('予約の更新に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  if (!reservation) return null;

  return (
    <div className="modal active" id="editModal">
      <div className="modal-content">
        <span className="close-modal" onClick={onClose}>
          &times;
        </span>
        <h2>予約の編集</h2>
        <form id="editReservationForm" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>予約日</label>
            <input
              type="date"
              id="editDate"
              value={formData.reservation_date}
              onChange={(e) =>
                setFormData({ ...formData, reservation_date: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label>予約時間</label>
            <input
              type="time"
              id="editTime"
              value={formData.reservation_time}
              onChange={(e) =>
                setFormData({ ...formData, reservation_time: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label>大人の人数</label>
            <input
              type="number"
              id="editAdultCount"
              min="0"
              value={formData.adult_count}
              onChange={(e) =>
                setFormData({ ...formData, adult_count: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label>子供の人数</label>
            <input
              type="number"
              id="editChildCount"
              min="0"
              value={formData.child_count}
              onChange={(e) =>
                setFormData({ ...formData, child_count: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label>席種</label>
            <select
              id="editSeatType"
              value={formData.seat_type}
              onChange={(e) =>
                setFormData({ ...formData, seat_type: e.target.value })
              }
              required
            >
              <option value="counter">カウンター</option>
              <option value="table">テーブル{parseInt(formData.adult_count) >= 5 ? '（2卓）' : ''}</option>
            </select>
            {formData.seat_type === 'table' && parseInt(formData.adult_count) >= 5 && (
              <p style={{ color: '#ffa500', fontSize: '0.85rem', marginTop: '5px' }}>
                ※大人5名以上のため、テーブル2卓を使用します
              </p>
            )}
          </div>
          <div className="form-group">
            <label>ステータス</label>
            <select
              id="editStatus"
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
              required
            >
              <option value="pending">店舗確認中</option>
              <option value="confirmed">確定</option>
              <option value="cancelled">キャンセル</option>
              <option value="visited">来店済み</option>
              <option value="no_show">無断キャンセル</option>
            </select>
          </div>
          <div className="modal-buttons">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? '保存中...' : '保存'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
            >
              キャンセル
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditReservationModal;

